import type { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, type Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

/**
 * Works around a WebKit bug that sends file uploads with an empty body (Content-Length: 0) when the
 * page is controlled by a service worker. The API rejects these with "Unexpected end of form".
 * https://bugs.webkit.org/show_bug.cgi?id=319985
 *
 * WebKit hands the network process the path of a disk-backed File, which its sandbox is not allowed
 * to read. This happens for both XHR and fetch, even when the service worker does not handle the
 * request. Copying the bytes into memory in the page avoids the file path entirely.
 *
 * The request also carries `ngsw-bypass` so the Angular service worker does not re-issue it. A query
 * param is used rather than the header so cross-origin API requests don't need the header added to
 * the CORS allowed headers.
 */
@Injectable()
export class FileUploadInterceptor implements HttpInterceptor {
  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const { body } = req;
    if (!(body instanceof FormData || body instanceof Blob)) {
      return next.handle(req);
    }

    const inMemoryBody = body instanceof FormData ? copyFormDataIntoMemory(body) : copyBlobIntoMemory(body);
    return from(inMemoryBody).pipe(
      switchMap((copy) => next.handle(req.clone({ body: copy, setParams: { 'ngsw-bypass': 'true' } })))
    );
  }
}

async function copyBlobIntoMemory(blob: Blob): Promise<Blob> {
  const bytes = await blob.arrayBuffer();
  if (blob instanceof File) {
    return new File([bytes], blob.name, { type: blob.type, lastModified: blob.lastModified });
  }
  return new Blob([bytes], { type: blob.type });
}

async function copyFormDataIntoMemory(formData: FormData): Promise<FormData> {
  const entries: [string, FormDataEntryValue][] = [];
  formData.forEach((value, key) => {
    entries.push([key, value]);
  });

  const copiedEntries = await Promise.all(
    entries.map(async ([key, value]): Promise<[string, FormDataEntryValue]> => {
      if (typeof value === 'string') {
        return [key, value];
      }
      return [key, (await copyBlobIntoMemory(value)) as File];
    })
  );

  const copy = new FormData();
  for (const [key, value] of copiedEntries) {
    copy.append(key, value);
  }
  return copy;
}
